import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { Formik, Field } from 'formik';
import { FieldCustom, FieldFile, FieldDate } from 'components/molecules';
import { Form, Button } from 'components/atoms';

import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { toast } from 'react-toastify';
import { Toast } from 'containers';
import { useMutation, queryCache } from 'react-query'
import { uploadFile as postFile, FILES_KEY } from 'services/requests'

const FILE_SIZE_MB = 20;

export const FileSchema = Yup.object().shape({
  file: Yup.mixed()
    .required('Campo obrigatório')
    .test(
      'fileSize',
      `Ficheiro demasiado grande, máximo de ${FILE_SIZE_MB}MB`,
      value => {
        const size = value ? value.size : 0;
        return size <= FILE_SIZE_MB * 1024 * 1024;
      }
    )
    .test(
      'fileFormat',
      'Formato inválido, para carregar imagens utilize a galeria.',
      value => value && value.type.indexOf('image') === -1
    )
    .test(
      'fileFormat',
      'O formato .exe não é válido',
      value => value && value.type.indexOf('.exe') === -1
    ),
  title: Yup.string()
    .max(255, 'Máximo de 255 caracteres')
    .required('Campo obrigatório'),
  description: Yup.string()
    .max(255, 'Máximo de 255 caracteres')
    .required('Campo obrigatório'),
  createdAt: Yup.string().required('Campo obrigatório'),
  expiresAt: Yup.string().required('Campo obrigatório'),
});

const FileUploadForm = ({
  initialValues, subweb, lang, onSubmit, onDiscard
}) => {
  const [uploadFile, { isLoading }] = useMutation(postFile, {
    onSuccess: (data) => {
      queryCache.invalidateQueries(FILES_KEY)
      onSubmit(data.data)
      toast.success(
        <Toast
          type="success"
          title="Upload concluído"
          description="O ficheiro foi gravado com sucesso."
        />
      );
    },
    onError: (err) => {
      toast.error(
        <Toast
          type="error"
          title="Erro a fazer upload"
          description={err.message}
        />
      );
    }
  })

  const computedInitialValues = useMemo(() => {
    const tenYearsLater = new Date(
      new Date().setFullYear(new Date().getFullYear() + 10)
    ).toISOString()

    return {
      ...initialValues,
      createdAt: new Date().toISOString(),
      expiresAt: initialValues.expiresAt || tenYearsLater,
    }
  }, [initialValues])

  const isInitialValid = useMemo(
    () => FileSchema.isValidSync(computedInitialValues),
    [computedInitialValues]
  );

  const handleSubmit = async values => {
    const {
      file,
      title,
      description,
      expiresAt,
    } = values;

    uploadFile({
      file,
      title,
      description,
      expiresAt,
      subweb,
      language: lang,
    })
  };

  return (
    <Formik
      initialValues={computedInitialValues}
      isInitialValid={isInitialValid}
      validationSchema={FileSchema}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
    >
      {({ isSubmitting }) => (
        <Form>
          <Field
            name="file"
            title="Ficheiro"
            type="file"
            component={FieldFile}
            accept="video/*,audio/*,application/*,text/*,.doc,.docx,.xlsx,.xls,.ppt,.pptx"
          />
          <Field
            name="title"
            title="Título"
            component={FieldCustom}
            maxLetters={255}
            info="título do ficheiro"
          />
          <Field
            name="description"
            title="Descrição"
            component={FieldCustom}
            maxLetters={255}
            info="descrição do ficheiro"
          />
          <FieldsSplit>
            <Field
              name="createdAt"
              title="Data de criação"
              type="date"
              component={FieldDate}
              isDisabled
            />
            <Field
              name="expiresAt"
              title="Data de expiração"
              type="date"
              component={FieldDate}
              info="10 anos por default"
            />
          </FieldsSplit>

          <Actions>
            <Button
              type="button"
              modifiers={['marginRight', 'capitalize']}
              onClick={onDiscard}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              modifiers={['primary', 'capitalize']}
              loading={isSubmitting || isLoading}
            >
              Carregar
            </Button>
          </Actions>
        </Form>
      )}
    </Formik>
  );
};

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.l};
`

const FieldsSplit = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: ${props => props.theme.spacing.xl1};
`

FileUploadForm.propTypes = {
  onSubmit: PropTypes.func,
  onDiscard: PropTypes.func,
  initialValues: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    file: PropTypes.shape({}),
  }),
  subweb: PropTypes.string,
  lang: PropTypes.string,
};

FileUploadForm.defaultProps = {
  onSubmit: () => null,
  onDiscard: () => null,
  initialValues: {
    title: '',
    description: '',
    file: null,
    expiresAt: null,
    createdAt: null,
  },
  subweb: 'ua',
  lang: 'pt',
};

export default FileUploadForm;
